<template>
  <div
    v-if="status === 'success' && data && Object.keys(data).length > 0"
    class="worldRanking"
  >
    <SectionTitle :title="title" />
    <div class="contents">
      <NuxtLink
        v-for="info, i in Object.values(data)"
        :key="i"
        :to="{
          path: isExternalLink ? `${runtimeConfig.app.baseURL}${info.pointId.replace(/^\//, '')}` : info.pointId,
          query: {
            fm: 'ranking'
          }
        }"
        :external="isExternalLink"
        class="link"
        :data-rank="info.rank"
      >
        <p class="rank">
          第<span class="rank__num">{{ info.rank }}</span>位
        </p>
        <div class="name">
          <figure
            v-if="isDisplayFlag"
            class="flag"
          >
            <img
              :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${info.cc}.webp`"
              width="20"
              height="20"
              loading="lazy"
              alt=""
            >
          </figure>
          <p
            v-if="isDisplayFlag"
            class="text"
          >
            <span>{{ info.pointName }}</span>
          </p>
          <p
            v-else
            class="text"
          >
            {{ info.pointName }}
          </p>
        </div>
        <div class="data">
          <figure class="weather">
            <img
              :src="`${runtimeConfig.public.gvsUrl}/onebox/img/wxicon/${info.wx}.webp`"
              width="152"
              height="112"
              loading="lazy"
              alt=""
            >
          </figure>
          <div class="temp">
            <p class="high">
              {{ info.maxt }}<span class="unit">℃</span>
            </p>
            <p class="low">
              {{ info.mint }}<span class="unit">℃</span>
            </p>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup>
import countryIdList from '~/data/table/country_id_list.json'

const props = defineProps({
  targetDisplayType: {
    type: String,
    required: true
  },
  targetId: {
    type: String,
    required: true
  },
  targetPointType: {
    type: String,
    required: true
  },
  now: {
    type: Number,
    required: true
  }
})

const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const isExternalLink = ref(isWebViewFlag)

const { data, status } = await useAsyncData(
  `ranking:${props.targetDisplayType}:${props.targetId}:${props.targetPointType}`,
  async () => {
    // setting.json 取得
    const settingRes = await $fetch('/api/ranking/setting')
    if (!settingRes || !settingRes[props.targetDisplayType] || !settingRes[props.targetDisplayType][props.targetId] || !settingRes[props.targetDisplayType][props.targetId].includes(props.targetPointType)) {
      return []
    }

    // ランキング情報取得
    const rankingRes = await $fetch('/api/ranking/info', {
      params: {
        targetId: props.targetId,
        targetType: props.targetPointType
      }
    })
    if (!rankingRes || !rankingRes.ranking || rankingRes.ranking.length === 0) {
      return []
    }

    // 天気情報 + 地点情報取得
    const [wxRes, infoRes] = await Promise.all([
      $fetch('/api/point/weather', {
        params: {
          pointId: rankingRes.ranking.join(',')
        }
      }),
      $fetch('/api/point/info', {
        params: {
          pointId: rankingRes.ranking.join(',')
        }
      })
    ])
    const formatWxRes = formatMajorCityWeather(rankingRes.ranking, wxRes, props.now)

    // ランキング情報と天気情報を結合
    const rankingList = rankingRes.ranking.map((pointId, i) => {
      const wx = formatWxRes[pointId]
      const info = infoRes[pointId]
      return {
        pointId,
        pointName: info.point_ja_description || info.point_ja,
        cc: info.cc,
        ...wx,
        // 1始まりの順位として設定
        rank: i + 1
      }
    })
    return rankingList
  }
)

const title = computed(() => {
  const name = props.targetDisplayType === 'area' ? areaLabelMap[props.targetId.replaceAll('/', '')] : countryIdList[props.targetId].country_ja
  return `人気の${props.targetPointType === 'city' ? '都市' : '観光地'}ランキング（${name}）`
})
const isDisplayFlag = computed(() => {
  return props.targetDisplayType === 'area'
})
</script>

<style lang="scss" scoped>
.worldRanking {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .contents {
    display: flex;
    gap: 5px;
    padding-bottom: 5px;
    padding-right: 5px;
    overflow-x: scroll;
    &::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    &::-webkit-scrollbar-track {
      border-radius: 100vmax;
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background: #438fec;
    }
  }
  .link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    min-width: 150px;
    padding: 15px 5px 5px;
    border-radius: 10px;
    background-color: #6981AA;
    box-shadow: 3px 3px 2px 0 rgb(130 130 130 / .25);
    &[data-rank="1"] {
      background-color: #DEBB5B;
    }
    &[data-rank="2"] {
      background-color: #ADB9BD;
    }
    &[data-rank="3"] {
      background-color: #D17F58;
    }
  }
  .rank {
    display: flex;
    align-items: baseline;
    gap: 3px;
    padding-inline: 20px;
    color: #FFF;
    font-size: 14px;
    font-weight: 700;
    line-height: 24px;
    background-image: url('~/assets/images/common/icon_rank_left.svg'), url('~/assets/images/common/icon_rank_right.svg');
    background-repeat: no-repeat;
    background-position: left center, right center;
    background-size: contain;
    user-select: none;
    &__num {
      font-family: "Hiragino Sans";
      font-size: 19px;
    }
  }
  .name {
    display: flex;
    align-items: center;
    gap: 5px;
    flex: 1;
    min-height: 2.6em;
    font-size: 17px;
    .flag {
      width: 20px;
      height: 20px;
      border-radius: 3px;
      border: solid 2px #FFF;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        vertical-align: top;
      }
    }
    .text {
      flex: 1;
      overflow: hidden;
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      max-height: 2.6em;
      color: #FFF;
      font-size: 17px;
      font-weight: 800;
      line-height: 1.3em;
      user-select: none;
      line-break: strict;
      span {
        display: block;
        width: 100%;
      }
    }
    &:has(.flag) {
      padding-inline: 6px;
    }
    &:not(:has(.flag)) .text {
      text-align: center;
    }
  }
  .data {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-top: auto;
    padding: 5px;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    background-color: #FFF;
    .weather {
      width: 32px;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .temp {
      display: flex;
      align-items: center;
      gap: 10px;
      p {
        font-family: "Hiragino Sans";
        font-size: 14px;
        font-weight: 400;
        line-height: 1.5em;
        user-select: none;
      }
      .high {
        color: #F64D00;
      }
      .low {
        position: relative;
        color: #0075F3;
        &::before {
          position: absolute;
          top: 50%;
          left: -5px;
          transform: translate(-50%, -50%);
          width: 1px;
          height: 13px;
          background-color: #d6d6d6;
          content: '';
        }
      }
      .unit {
        font-size: 12px;
      }
    }
  }
  @media (any-hover: hover) {
    .link {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
  }
}
.main {
  .worldRanking {
    @media (min-width: 830px) {
      .contents {
        gap: 10px;
      }
      .link {
        min-width: 200px;
        box-shadow: 4px 4px 2px 0 rgb(130 130 130 / .25);
      }
      .rank {
        padding-inline: 26px;
        font-size: 15px;
        line-height: 32px;
        &__num {
          font-size: 25px;
        }
      }
      .name {
        gap: 5px;
        min-height: 2.6em;
        font-size: 20px;
        .flag {
          width: 26px;
          height: 26px;
        }
        .text {
          font-size: 20px;
        }
        &:has(.flag) {
          padding-inline: 8px;
        }
      }
      .data {
        .weather {
          width: 43px;
        }
        .temp {
          p {
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>
